import { useEffect, useState } from 'react';
import { api, type HousingCapacity as Capacity } from '../lib/api';
import { navigate } from '../lib/router';
import { addDays, dayLabel } from '../lib/dates';

const short = (d: string) => dayLabel(d).replace(/^\w+, /, '');

/**
 * How full OSMD's houses will be (D100).
 *
 * OSMD could not predict this: some physicians drive in from their own homes and some take a
 * bed, and until now nobody was asked which. The number here is only as good as those answers,
 * so what is NOT known is shown beside what is, never folded into the percentage.
 *
 * Read per pool, not per hospital. Beds belong to houses and houses serve particular hospitals
 * — the Newburgh house takes physicians from three Deaconess sites with four bedrooms between
 * them (D53), so those three are one row. One company-wide percentage would read comfortably in
 * exactly the week one house is oversubscribed and another is half empty.
 */
export function HousingCapacity({ week }: { week: string }) {
  const [data, setData] = useState<Capacity | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setData(null);
    setProblem(null);
    api
      .housingCapacity(week)
      .then((d) => live && setData(d))
      .catch((e: Error) => live && setProblem(e.message));
    return () => {
      live = false;
    };
  }, [week]);

  const go = (w: string) => navigate(`/admin/logistics/housing?week=${w}`);

  return (
    <section className="logistics">
      <div className="gridtools noprint">
        <div className="quarterpicker">
          <button type="button" onClick={() => go(addDays(week, -7))} aria-label="Previous week">‹</button>
          <h1>Housing — Tue {short(week)} – Tue {short(addDays(week, 7))}</h1>
          <button type="button" onClick={() => go(addDays(week, 7))} aria-label="Next week">›</button>
        </div>
        <button type="button" className="linklike" onClick={() => window.print()}>Print / save as PDF</button>
      </div>

      {problem && <p className="error">{problem}</p>}
      {!data && !problem && <p className="muted">Working it out…</p>}

      {data && (
        <>
          {!data.pools.length && <p className="muted">No houses and nobody expecting a bed this week.</p>}

          {data.pools.map((pool) => {
            const key = pool.sites.map((s) => s.id).join('|');
            const over = pool.over > 0;
            return (
              <section key={key} className={`costgroup${over ? ' overbooked' : ''}`}>
                <h3>
                  {pool.sites.map((s) => s.name).join(' · ')}{' '}
                  {pool.percent === null ? (
                    <span className="pill tiny warn">no residence</span>
                  ) : (
                    <span className={`pill tiny${over ? ' warn' : ''}`}>{pool.percent}% full</span>
                  )}
                </h3>
                <p className="muted small">
                  {pool.residences.length
                    ? `${pool.residences.map((r) => `${r.name} (${r.beds} ${r.beds === 1 ? 'bed' : 'beds'})`).join(', ')}${pool.residences.length > 1 ? ` — ${pool.beds} beds in total.` : '.'}`
                    : 'No OSMD house serves these hospitals.'}{' '}
                  {over
                    ? `${pool.over} more ${pool.over === 1 ? 'physician' : 'physicians'} than beds on the busiest night.`
                    : `Busiest night needs ${pool.worstNight} of ${pool.beds}.`}
                </p>
                <table className="grid">
                  <thead>
                    <tr>
                      <th scope="col">Night</th>
                      <th scope="col" className="num">Beds needed</th>
                      <th scope="col" className="num">Beds free</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pool.byNight.map((n) => (
                      <tr key={n.date} className={n.needed > pool.beds ? 'over' : ''}>
                        <th scope="row">{dayLabel(n.date)}</th>
                        <td className="num">{n.needed}</td>
                        {/* Never a negative: "-1 free" is not a count of anything. A night that
                            is short says so in words, because that is the row to act on. */}
                        <td className="num">{n.needed > pool.beds ? <strong className="warn">{n.needed - pool.beds} short</strong> : pool.beds - n.needed}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {pool.physicians.length > 0 && (
                  <p className="muted small">
                    Expecting a bed: {pool.physicians.map((x) => `${x.firstName} ${x.lastName}`).join(', ')}.
                  </p>
                )}
              </section>
            );
          })}

          {/* Never folded into a percentage above. A capacity figure that quietly guesses at
              the people who have not answered is one nobody can act on. */}
          {data.unanswered.length > 0 && (
            <section className="costgroup">
              <h3>
                Not answered yet <span className="pill tiny warn">{data.unanswered.length}</span>
              </h3>
              <p className="muted small">
                These physicians are working this week but have not said where they are staying, so they are in none of the counts above.
                Their "before you go" form now asks before it will save.
              </p>
              <table className="grid">
                <thead>
                  <tr>
                    <th scope="col">Physician</th>
                    <th scope="col">Hospital</th>
                    <th scope="col">Trip starts</th>
                  </tr>
                </thead>
                <tbody>
                  {data.unanswered.map((u) => (
                    <tr key={`${u.physician.id}|${u.stintStart}`}>
                      <th scope="row">{u.physician.firstName} {u.physician.lastName}</th>
                      <td>{u.site}</td>
                      <td>{dayLabel(u.stintStart)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </>
      )}
    </section>
  );
}
